import { Injectable } from '@angular/core';
import type { Auth, User } from 'firebase/auth';
import { GoogleAuthProvider } from 'firebase/auth';

import { environment } from '../../environments/environment';
import { BetaAuthService, BetaAuthUser } from './beta-auth.service';

/**
 * Modular Firebase Auth helper (Google popup), patterned after central-ui FirebaseService.
 * After Google success, exchanges the ID token via Labs beta provider-auth.
 */
@Injectable({ providedIn: 'root' })
export class FirebaseAuthService {
	private auth: Auth | null = null;
	private firebaseApp: unknown = null;
	private authInitialized = false;
	public currentUser: User | null = null;

	constructor(private readonly betaAuth: BetaAuthService) {}

	public async signInWithGoogle(): Promise<BetaAuthUser> {
		const auth = await this.getAuth();
		if (!auth) {
			throw new Error('Firebase Auth is not available.');
		}
		const { signInWithPopup } = await import('firebase/auth');
		const provider = new GoogleAuthProvider();
		provider.setCustomParameters({ prompt: 'select_account' });
		const result = await signInWithPopup(auth, provider);
		this.currentUser = result.user;
		const idToken = await result.user.getIdToken();
		return this.betaAuth.providerAuth(idToken);
	}

	public async signOut(): Promise<void> {
		const auth = this.auth ?? (await this.getAuth());
		if (!auth) {
			return;
		}
		await auth.signOut();
		this.currentUser = null;
	}

	public async getAuth(): Promise<Auth | null> {
		if (!this.auth && !this.authInitialized) {
			await this.initializeAuth();
		}
		return this.auth;
	}

	private get firebaseConfig() {
		return environment.firebase;
	}

	private isFirebaseConfigValid(): boolean {
		const cfg = this.firebaseConfig;
		return !!(
			cfg?.apiKey &&
			cfg.apiKey !== 'YOUR_API_KEY' &&
			cfg.authDomain &&
			cfg.projectId
		);
	}

	private async initializeAuth(): Promise<void> {
		if (this.authInitialized) {
			return;
		}
		if (!this.isFirebaseConfigValid()) {
			console.warn(
				'[app-launcher] Firebase config looks incomplete; Google sign-in disabled.',
			);
			return;
		}
		try {
			const { initializeApp, getApps } = await import('firebase/app');
			const { getAuth, onAuthStateChanged } = await import('firebase/auth');

			if (!this.firebaseApp) {
				const apps = getApps();
				this.firebaseApp =
					apps.length === 0
						? initializeApp(this.firebaseConfig)
						: apps[0];
			}
			this.auth = getAuth(this.firebaseApp as Parameters<typeof getAuth>[0]);
			this.authInitialized = true;
			onAuthStateChanged(this.auth, (user) => {
				this.currentUser = user;
			});
		} catch (error) {
			console.error('[app-launcher] Firebase Auth init failed', error);
		}
	}
}
